import { create } from 'zustand'
import { persist } from 'zustand/middleware'

const usePreferencesStore = create(
  persist(
    (set, get) => ({
      sidebarCollapsed: false,
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      // Column visibility keyed by page (e.g., 'matters', 'contacts')
      columnVisibility: {},
      setColumnVisibility: (page, visibility) =>
        set((state) => ({
          columnVisibility: { ...state.columnVisibility, [page]: visibility },
        })),

      // Page size keyed by page
      pageSize: {},
      setPageSize: (page, size) =>
        set((state) => ({
          pageSize: { ...state.pageSize, [page]: size },
        })),

      // Column widths keyed by page (CC-49380) — react-table columnSizing map: { columnId: px }
      columnWidths: {},
      setColumnWidths: (page, widths) =>
        set((state) => ({
          columnWidths: { ...state.columnWidths, [page]: widths },
        })),
      getColumnWidths: (page) => get().columnWidths[page] || {},

      // Column order keyed by page (CC-49459) — array of column ids in display order.
      columnOrder: {},
      setColumnOrder: (page, order) =>
        set((state) => ({
          columnOrder: { ...state.columnOrder, [page]: order },
        })),
      getColumnOrder: (page) => get().columnOrder[page] || null,

      // Sort state keyed by page (CC-49336) — react-table style [{ id, desc }].
      sorting: {},
      setSorting: (page, sorting) =>
        set((state) => ({
          sorting: { ...state.sorting, [page]: sorting },
        })),
      getSorting: (page) => get().sorting[page] || null,

      // Per-column alignment override keyed by page (CC-49735) —
      // { [columnId]: 'left' | 'center' | 'right' }. Layers over the report's
      // own metadata alignment; unset columns keep the CB default. Returns null
      // (not a fresh {}) when nothing is stored so callers can memoize on it.
      columnAlignment: {},
      setColumnAlignment: (page, colId, align) =>
        set((state) => {
          const forPage = { ...(state.columnAlignment[page] || {}) }
          if (align) forPage[colId] = align
          else delete forPage[colId]
          return { columnAlignment: { ...state.columnAlignment, [page]: forPage } }
        }),
      getColumnAlignment: (page) => get().columnAlignment[page] || null,

      // Per-report currency-symbol toggle (CC-49341 / CC-49743) — tri-state:
      // true = force $ on amount columns, false = strip $ from them, unset
      // (null) = leave the server display alone. Returns null when unset so
      // callers can distinguish "auto" from an explicit choice.
      currencySymbol: {},
      setCurrencySymbol: (page, on) =>
        set((state) => ({
          currencySymbol: { ...state.currencySymbol, [page]: !!on },
        })),
      getCurrencySymbol: (page) => get().currencySymbol[page] ?? null,

      // Per-page boolean layout flags (CC-49974) — `{ [page]: { [flag]: bool } }`.
      // Generic on purpose: the Balance Sheet's side-by-side toggle is the first
      // consumer, and a dedicated top-level map per future toggle would mean a new
      // store key (and a new entry in every test mock) each time. Returns null
      // when unset so a caller can tell "never chosen" from an explicit false.
      //
      // ⚠️ CROSS-SESSION NOTE: preferencesStore is a shared/contested file. This
      // addition is purely additive — no existing key or method changed — so
      // pages that don't call getPageFlag/setPageFlag are unaffected.
      pageFlags: {},
      setPageFlag: (page, flag, on) =>
        set((state) => ({
          pageFlags: {
            ...state.pageFlags,
            [page]: { ...(state.pageFlags[page] || {}), [flag]: !!on },
          },
        })),
      getPageFlag: (page, flag) => get().pageFlags[page]?.[flag] ?? null,

      // Named report views (CC-49336) — per page: { [name]: { filters,
      // columnVisibility, columnWidths, columnOrder, sorting } }. Per-user by
      // nature (localStorage); sharing is out of scope for v1.
      namedViews: {},
      getNamedViews: (page) => get().namedViews[page] || {},
      saveNamedView: (page, name, view) =>
        set((state) => ({
          namedViews: {
            ...state.namedViews,
            [page]: { ...(state.namedViews[page] || {}), [name]: view },
          },
        })),
      deleteNamedView: (page, name) =>
        set((state) => {
          const forPage = { ...(state.namedViews[page] || {}) }
          delete forPage[name]
          return { namedViews: { ...state.namedViews, [page]: forPage } }
        }),
      renameNamedView: (page, oldName, newName) =>
        set((state) => {
          const forPage = { ...(state.namedViews[page] || {}) }
          if (!(oldName in forPage) || !newName || newName === oldName) return {}
          forPage[newName] = forPage[oldName]
          delete forPage[oldName]
          return { namedViews: { ...state.namedViews, [page]: forPage } }
        }),

      // Per-user dashboard tile layouts (spec 038 Phase 2) keyed by dashboard
      // id → { [cellId]: { x, y, w, h } }. cb2-local (localStorage), like the
      // report column widths/order above — a personal arrangement, not the
      // shared CB definition, so no CanEdit permission is required to set one.
      dashboardLayouts: {},
      setDashboardLayout: (id, layout) =>
        set((state) => ({
          dashboardLayouts: { ...state.dashboardLayouts, [String(id)]: layout },
        })),
      getDashboardLayout: (id) => get().dashboardLayouts[String(id)] || null,
      resetDashboardLayout: (id) =>
        set((state) => {
          const dashboardLayouts = { ...state.dashboardLayouts }
          delete dashboardLayouts[String(id)]
          return { dashboardLayouts }
        }),

      // Reset a report/page to its default layout (CC-49380): drop persisted
      // visibility + widths + order + sorting (+ pageSize) for that page so it
      // falls back to defaults. Named views are kept — they're saved content,
      // not layout state.
      resetPagePreferences: (page) =>
        set((state) => {
          const columnVisibility = { ...state.columnVisibility }
          const columnWidths = { ...state.columnWidths }
          const pageSize = { ...state.pageSize }
          const columnOrder = { ...state.columnOrder }
          const sorting = { ...state.sorting }
          const currencySymbol = { ...state.currencySymbol }
          const columnAlignment = { ...state.columnAlignment }
          const pageFlags = { ...state.pageFlags }
          delete columnVisibility[page]
          delete columnWidths[page]
          delete pageSize[page]
          delete columnOrder[page]
          delete sorting[page]
          delete currencySymbol[page]
          delete columnAlignment[page]
          delete pageFlags[page]
          return { columnVisibility, columnWidths, pageSize, columnOrder, sorting, currencySymbol, columnAlignment, pageFlags }
        }),

      defaultPageSize: 50,
      setDefaultPageSize: (size) => set({ defaultPageSize: size }),

      // CC-49844: header-less "data only" client export (skip the title /
      // filters / generated rows in Excel). One per-user switch — firms that
      // export into other systems want it on everywhere, so it isn't keyed
      // by page.
      exportDataOnly: false,
      setExportDataOnly: (on) => set({ exportDataOnly: !!on }),
      getExportDataOnly: () => !!get().exportDataOnly,

      theme: 'light',
      setTheme: (theme) => set({ theme }),

      // `fallback` lets a page declare its own sensible default (DataGrid's
      // pageSize prop) without hard-coding it here. Purely additive: a caller
      // that passes nothing gets exactly the old behaviour. The user's own
      // stored choice still wins over both.
      getPageSize: (page, fallback) => {
        const state = get()
        return state.pageSize[page] || fallback || state.defaultPageSize
      },

      getColumnVisibility: (page) => {
        return get().columnVisibility[page] || {}
      },
    }),
    {
      name: 'cb2-preferences',
      // CC-49948: v0 persisted a report's METADATA-DERIVED default sort as if the
      // user had chosen it (the grids wrote `sorting` on mount). Anyone who had
      // opened a report before the default-sort fix therefore kept the old, wrong
      // sort forever — the stored value shadowed the corrected rule, which is why
      // the fix read as "no change at all" in QA. The grids no longer write an
      // unchosen sort; this one-time migration clears the ones already stored.
      // Only `sorting` is dropped — visibility, widths, order, alignment and
      // named views are untouched, and a user's real sort costs one click to redo.
      version: 1,
      migrate: (persisted, fromVersion) => {
        if (!persisted || fromVersion >= 1) return persisted
        return { ...persisted, sorting: {} }
      },
    }
  )
)

export default usePreferencesStore
